import { Input } from '@vkontakte/vkui';
import { useState, type ReactNode } from 'react';
import { formatDate, type BaseRecord, type PropertyState } from '../../api/cabinet';
import { t } from '../../i18n';
import {
  Action,
  ActionRow,
  Gap,
  Grow,
  Gutter,
  Muted,
  Notice,
  Prose,
  SearchField,
  TabRow,
  type StatusTone,
  type TabRowOption,
} from '../../shared/ui';

/**
 * Общее у трёх экранов имущества — склада, питомцев и способностей.
 *
 * Все три устроены одинаково: строка сущности плюс история версий, публике
 * видна published, правка заводит новую версию и снова идёт через проверку,
 * удаление отправляет в архив. Различаются только поля версии — их рисует
 * сам экран, а состояния, тексты и подтверждение архивирования живут здесь:
 * три копии этой логики разъехались бы на первой же правке.
 */

export const PROPERTY_STATES: readonly TabRowOption<PropertyState>[] = [
  { value: 'published', label: t('property.state.published') },
  { value: 'requests', label: t('property.state.requests') },
  { value: 'archived', label: t('property.state.archived') },
];

/**
 * Поиск по имуществу и кнопка заведения одной полосой.
 *
 * Кнопка приходит слотом, а не флагом: у склада, питомцев и способностей
 * подпись своя, а место у неё одно — справа от поля, как в макетах.
 */
export function PropertySearch({
  value,
  onChange,
  placeholder,
  action,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  action?: ReactNode;
}) {
  return (
    <SearchField value={value} onChange={onChange} placeholder={placeholder} action={action} />
  );
}

export interface PropertyStatus {
  /** Короткая подпись для значка: её читают боковым зрением. */
  badge: string;
  /** Что это значит и что делать. Не всегда есть — у живой вещи объяснять нечего. */
  hint: string | null;
  tone: StatusTone;
}

/**
 * Показана принятая версия, и поверх неё ничего не едет.
 *
 * Вынесено, потому что describeAbility повторяла ровно эти три условия у себя:
 * у способности поверх общего состояния есть ещё и срок. Разъехались бы молча.
 */
export function isLivePublished(entity: BaseRecord): boolean {
  return entity.deletedAt === null && entity.status === 'approved' && entity.pendingSince === null;
}

/**
 * Состояние вещи одним значком вместо строки текста.
 *
 * Пять веток, четыре цвета: «показана прошлая версия» — это тот же жёлтый
 * ожидания, что и «на проверке», разница только в пояснении.
 */
export function describeProperty(entity: BaseRecord): PropertyStatus {
  if (entity.deletedAt !== null) {
    return {
      badge: t('common.state.archived'),
      hint: entity.deleteReason ?? t('property.archive.noReason'),
      tone: 'archived',
    };
  }

  if (entity.status === 'rejected') {
    return {
      badge: t('property.state.badge.rejected'),
      hint: t('property.hint.rejected'),
      tone: 'rejected',
    };
  }

  if (entity.status !== 'approved') {
    return {
      badge: t('common.state.pending'),
      hint: t('property.hint.submitted', { date: formatDate(entity.submittedAt) }),
      tone: 'pending',
    };
  }

  if (entity.pendingSince !== null) {
    return {
      badge: t('property.state.badge.editPending'),
      hint: t('property.hint.editPending', { date: formatDate(entity.pendingSince) }),
      tone: 'pending',
    };
  }

  return { badge: t('common.state.published'), hint: null, tone: 'published' };
}

/**
 * Что показываем: принятое, заявки или архив. Это раздел, а не срез — три
 * разных списка под одной шапкой, — поэтому ряд подчёркнутый и стоит первым,
 * над поиском.
 */
export function PropertyModeSwitch({
  state,
  onChange,
  disabled,
}: {
  state: PropertyState;
  onChange: (state: PropertyState) => void;
  disabled?: boolean;
}) {
  return <TabRow value={state} options={PROPERTY_STATES} onChange={onChange} disabled={disabled} />;
}

export interface PropertyActionsProps {
  /** «Предмет», «Питомца», «Способность» — в винительном падеже. */
  subjectAccusative: string;
  busy: boolean;
  confirming: boolean;
  onEdit: () => void;
  onAskArchive: () => void;
  onCancelArchive: () => void;
  onArchive: (reason: string) => void;
}

/**
 * Кнопки карточки и подтверждение архивирования.
 *
 * Подтверждение спрашиваем прямо в карточке: попап пришлось бы вешать на
 * SplitLayout, а он собирается выше по дереву.
 */
export function PropertyActions({
  subjectAccusative,
  busy,
  confirming,
  onEdit,
  onAskArchive,
  onCancelArchive,
  onArchive,
}: PropertyActionsProps) {
  const [reason, setReason] = useState('');

  // Кнопки в РЯД, а не стопкой. Вертикальный ButtonGroup давал две полосы во
  // всю ширину под каждой карточкой у всех трёх сущностей разом.
  if (!confirming) {
    return (
      <>
        {/* Без значков: в макетах имущества на этих двух кнопках их нет, а
            на 163 px значок съедает четверть подписи. */}
        <Action tone="accent" size="m" stretched disabled={busy} onClick={onEdit}>
          {t('property.action.edit')}
        </Action>
        <Action tone="quiet" size="m" stretched disabled={busy} onClick={onAskArchive}>
          {t('property.action.archive')}
        </Action>
      </>
    );
  }

  return (
    <Grow>
      <Prose>{t('property.archive.confirm', { subject: subjectAccusative })}</Prose>
      <Muted>{t('property.archive.note')}</Muted>
      <Gap step="s2" />
      <Input
        placeholder={t('property.archive.reasonPlaceholder')}
        value={reason}
        disabled={busy}
        onChange={(event) => setReason(event.target.value)}
      />
      <Gap step="s2" />
      <ActionRow>
        <Action tone="action" size="m" stretched disabled={busy} onClick={() => onArchive(reason)}>
          {t('property.archive.submit')}
        </Action>
        <Action tone="quiet" size="m" stretched disabled={busy} onClick={onCancelArchive}>
          {t('property.archive.cancel')}
        </Action>
      </ActionRow>
    </Grow>
  );
}

/** Пояснение под кнопкой «добавить»: одно на все три экрана. */
export function ReviewNote({ children }: { children: React.ReactNode }) {
  return (
    /*
      Gutter, а не Div: коробка нужна только ради поля ПО БОКАМ. У Div есть ещё
      и вертикальное, и оно складывалось с отступом раздела — над полосой
      выходило 20 px вместо двенадцати из макета.
    */
    <Gutter>
      {/* Полосой, а не голым текстом: в макете это врезка со своей засечкой, и
          голым текстом она читалась как часть формы. */}
      <Notice tone="pending">{children}</Notice>
    </Gutter>
  );
}
